var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { defaultHeaderInterpreter } from '../header/interpreter';
import { CacheRequestInterceptor } from '../interceptors/request';
import { CacheResponseInterceptor } from '../interceptors/response';
import { isStorage } from '../storage/build';
import { buildMemoryStorage } from '../storage/memory';
import { defaultKeyGenerator } from '../util/key-generator';
const symbolKey = Symbol();
/**
 * Apply the caching interceptors for a already created axios instance.
 *
 * @example
 *
 * ```ts
 * import Axios from 'axios';
 * import { setupCache, AxiosCacheInstance } from 'axios-cache-interceptor';
 *
 * // instance will have our custom typings from the return of this function
 * const instance = setupCache(
 *   Axios.create({
 *     // Axios options
 *   }),
 *   {
 *     // Axios-cache-interceptor options
 *   }
 * );
 *
 * // OR
 *
 * const instance = axios.create({
 *   // Axios options
 * }) as AxiosCacheInstance;
 *
 * // As this functions returns the same axios instance but only with
 * // different typings, you can ignore the function return.
 * setupCache(instance, {
 *   // Axios-cache-interceptor options
 * });
 * ```
 *
 * @param axios The already created axios instance
 * @param config The config for the caching interceptors
 * @returns The same instance with better typescript types.
 */
export function setupCache(axios, _a = {}) {
    var { storage, generateKey, waiting, headerInterpreter, requestInterceptor, responseInterceptor } = _a, cacheOptions = __rest(_a, ["storage", "generateKey", "waiting", "headerInterpreter", "requestInterceptor", "responseInterceptor"]);
    const axiosCache = axios;
    axiosCache.storage = storage || buildMemoryStorage();
    if (!isStorage(axiosCache.storage)) {
        throw new Error('Use buildStorage()');
    }
    axiosCache.generateKey = generateKey || defaultKeyGenerator;
    axiosCache.waiting = waiting || {};
    axiosCache.headerInterpreter = headerInterpreter || defaultHeaderInterpreter;
    axiosCache.requestInterceptor =
        requestInterceptor || new CacheRequestInterceptor(axiosCache);
    axiosCache.responseInterceptor =
        responseInterceptor || new CacheResponseInterceptor(axiosCache);
    // CacheRequestConfig values
    axiosCache.defaults = Object.assign(Object.assign({}, axios.defaults), { cache: Object.assign({ ttl: 1000 * 60 * 5, interpretHeader: false, methods: ['get'], cachePredicate: { statusCheck: [200, 399] }, etag: false, modifiedSince: false, update: {} }, cacheOptions) });
    // Apply interceptors
    axiosCache.requestInterceptor.use();
    axiosCache.responseInterceptor.use();
    // @ts-expect-error - internal only
    axiosCache[symbolKey] = 1;
    return axiosCache;
}
/** @deprecated */
export const useCache = setupCache;
/** @deprecated */
export const createCache = setupCache;
/**
 * Detects if the given parameter has caching enabled. The only way to this return true is
 * by using the {@link setupCache} function.
 *
 * @param axios The axios instance to use
 * @returns True if the axios instance is using the caching interceptor
 */
export const isAxiosCacheInterceptor = (axios) => !!axios && !!axios[symbolKey];
